import { useCallback } from 'react';
import { getPunchListReportImages } from '../api/api';
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  IMPACT_LABELS,
  DEFAULT_PUNCH_LIST_CSV_COLUMN_KEYS,
  formatUnitLabel,
  type PunchListCsvColumn,
  type PunchListCsvColumnKey,
  type PunchListItemSummary,
  type PunchListReportImage,
} from '../types';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatReportDate = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

export const resolvePunchListReportImageUrl = (value: string, appOrigin: string) => {
  try {
    const url = new URL(value, appOrigin);
    if (url.pathname.startsWith('/api/v1/photos/proxy/')) {
      return new URL(`${url.pathname}${url.search}${url.hash}`, appOrigin).href;
    }
    return url.href;
  } catch {
    return value;
  }
};

export const PUNCH_LIST_CSV_COLUMNS: PunchListCsvColumn[] = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'punch_manager', label: 'Punch Manager' },
  { key: 'final_approver', label: 'Final Approver' },
  { key: 'distribution_list', label: 'Distribution List' },
  { key: 'trade', label: 'Trade' },
  { key: 'responsible_trade_partner', label: 'Responsible Trade Partner' },
  { key: 'item_type', label: 'Type' },
  { key: 'location', label: 'Location' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'cost_impact', label: 'Cost Impact' },
  { key: 'schedule_impact', label: 'Schedule Impact' },
  { key: 'punch_item_link', label: 'Punch Item Link' },
  { key: 'created_by', label: 'Created By' },
  { key: 'created_at', label: 'Created At' },
  { key: 'completed_at', label: 'Completed At' },
  { key: 'completed_by', label: 'Completed By' },
];

export const PUNCH_LIST_CSV_COLUMN_KEYS = PUNCH_LIST_CSV_COLUMNS.map(
  ({ key }) => key,
);

interface CsvDateTimeOptions {
  locale?: string;
  timeZone?: string;
}

export const formatCsvDateTime = (
  value: string | null,
  options: CsvDateTimeOptions = {},
) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(options.locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: options.timeZone,
  }).format(date);
};

const formatUser = (user: PunchListItemSummary['assignee']) => {
  if (!user) return '';
  return `${user.first_name} ${user.last_name}`.trim() || user.email;
};

const getPunchListCsvValue = (
  item: PunchListItemSummary,
  column: PunchListCsvColumnKey,
  punchItemBaseUrl: string,
  dateTimeOptions: CsvDateTimeOptions,
) => {
  switch (column) {
    case 'title':
      return item.title;
    case 'description':
      return item.description;
    case 'status':
      return STATUS_LABELS[item.status];
    case 'priority':
      return PRIORITY_LABELS[item.priority];
    case 'assignee':
      return formatUser(item.assignee);
    case 'punch_manager':
      return formatUser(item.punch_manager);
    case 'final_approver':
      return formatUser(item.final_approver);
    case 'distribution_list':
      return item.distribution_list.map(formatUser).join('; ');
    case 'trade':
      return item.trade?.name ?? '';
    case 'responsible_trade_partner':
      return item.responsible_trade_partner?.company_name ?? '';
    case 'item_type':
      return item.item_type?.name ?? '';
    case 'location':
      return item.location ? formatUnitLabel(item.location) : '';
    case 'due_date':
      return formatCsvDateTime(item.due_date, dateTimeOptions);
    case 'cost_impact':
      return IMPACT_LABELS[item.cost_impact];
    case 'schedule_impact':
      return IMPACT_LABELS[item.schedule_impact];
    case 'punch_item_link':
      return punchItemBaseUrl ? `${punchItemBaseUrl}/${item.id}` : '';
    case 'created_by':
      return formatUser(item.created_by);
    case 'created_at':
      return formatCsvDateTime(item.created_at, dateTimeOptions);
    case 'completed_at':
      return formatCsvDateTime(item.completed_at, dateTimeOptions);
    case 'completed_by':
      return formatUser(item.completed_by) || item.completion_certifier_name;
  }
};

export const buildPunchListCsv = (
  items: PunchListItemSummary[],
  selectedColumns: PunchListCsvColumnKey[] = DEFAULT_PUNCH_LIST_CSV_COLUMN_KEYS,
  punchItemBaseUrl = '',
  dateTimeOptions: CsvDateTimeOptions = {},
) => {
  const columnsByKey = new Map(PUNCH_LIST_CSV_COLUMNS.map((column) => [column.key, column]));
  const columns = selectedColumns.flatMap((key) => {
    const column = columnsByKey.get(key);
    return column ? [column] : [];
  });
  const escapeCsv = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const rows = items.map((item) =>
    columns
      .map(({ key }) =>
        getPunchListCsvValue(item, key, punchItemBaseUrl, dateTimeOptions)
      )
      .map((cell) => escapeCsv(String(cell)))
      .join(','),
  );
  return [columns.map(({ label }) => escapeCsv(label)).join(','), ...rows].join('\n');
};

export const usePunchListReport = () => {
  const exportItemsToCsv = useCallback((
    items: PunchListItemSummary[],
    fileNamePrefix = 'punch-lists',
    selectedColumns: PunchListCsvColumnKey[] = DEFAULT_PUNCH_LIST_CSV_COLUMN_KEYS,
    punchItemBaseUrl = '',
  ) => {
    const csv = buildPunchListCsv(items, selectedColumns, punchItemBaseUrl);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeFileNamePrefix = fileNamePrefix
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'punch-lists';
    link.download = `${safeFileNamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  const generatePunchListPdfReport = useCallback(
    async (
      items: PunchListItemSummary[] | Promise<PunchListItemSummary[]>,
      reportTitle: string,
      projectName?: string,
      projectId?: number,
    ) => {
      const reportWindow = window.open('', '_blank', 'width=1100,height=900');
      if (!reportWindow) {
        await Promise.resolve(items).catch(() => []);
        return;
      }

      reportWindow.document.open();
      reportWindow.document.write(`<!doctype html>
<html>
<head><meta charset="utf-8" /><title>Preparing report…</title></head>
<body style="font-family:Arial,sans-serif;padding:32px;color:#0f172a;">Preparing punch list report…</body>
</html>`);
      reportWindow.document.close();

      let reportItems: PunchListItemSummary[];
      try {
        reportItems = await items;
      } catch {
        reportWindow.document.open();
        reportWindow.document.write(`<!doctype html>
<html>
<head><meta charset="utf-8" /><title>Unable to generate report</title></head>
<body style="font-family:Arial,sans-serif;padding:32px;color:#0f172a;">
  <h1 style="font-size:20px;">Unable to load punch list items</h1>
  <p>Please close this window and try generating the report again.</p>
</body>
</html>`);
        reportWindow.document.close();
        return;
      }

      let reportImages: PunchListReportImage[] = [];
      try {
        if (projectId && reportItems.length > 0) {
          reportImages = await getPunchListReportImages(
            projectId,
            reportItems.slice(0, 150).map((item) => item.id),
          );
        }
      } catch {
        reportWindow.document.open();
        reportWindow.document.write(`<!doctype html>
<html>
<head><meta charset="utf-8" /><title>Unable to generate report</title></head>
<body style="font-family:Arial,sans-serif;padding:32px;color:#0f172a;">
  <h1 style="font-size:20px;">Unable to load report images</h1>
  <p>Please close this window and try generating the report again.</p>
</body>
</html>`);
        reportWindow.document.close();
        return;
      }

      const imagesByItem = new Map<string, PunchListReportImage[]>();
      reportImages.forEach((image) => {
        const current = imagesByItem.get(image.item_id) ?? [];
        current.push(image);
        imagesByItem.set(image.item_id, current);
      });
      const statusOrder: Array<keyof typeof STATUS_LABELS> = ['OPEN', 'OVERDUE', 'COMPLETED', 'CLOSED'];
      const priorityOrder: Array<keyof typeof PRIORITY_LABELS> = ['HIGH', 'MEDIUM', 'LOW'];
      const statusColors: Record<string, string> = {
        OPEN: '#1d4ed8',
        OVERDUE: '#dc2626',
        COMPLETED: '#059669',
        CLOSED: '#6b7280',
      };
      const priorityColors: Record<string, string> = {
        HIGH: '#dc2626',
        MEDIUM: '#d97706',
        LOW: '#2563eb',
      };

      const statusCounts = statusOrder.map((status) => ({
        key: status,
        label: STATUS_LABELS[status],
        count: reportItems.filter((item) => item.status === status).length,
        color: statusColors[status],
      }));
      const priorityCounts = priorityOrder.map((priority) => ({
        key: priority,
        label: PRIORITY_LABELS[priority],
        count: reportItems.filter((item) => item.priority === priority).length,
        color: priorityColors[priority],
      }));
      const totalItems = reportItems.length;
      const openItems = statusCounts.find((entry) => entry.key === 'OPEN')?.count ?? 0;
      const overdueItems = statusCounts.find((entry) => entry.key === 'OVERDUE')?.count ?? 0;
      const completionRate = totalItems === 0
        ? 0
        : Math.round((((statusCounts.find((entry) => entry.key === 'COMPLETED')?.count ?? 0)
          + (statusCounts.find((entry) => entry.key === 'CLOSED')?.count ?? 0)) / totalItems) * 100);

      const assigneeCounts = new Map<string, number>();
      reportItems.forEach((item) => {
        const name = item.assignee
          ? `${item.assignee.first_name} ${item.assignee.last_name}`.trim()
          : 'Unassigned';
        assigneeCounts.set(name, (assigneeCounts.get(name) ?? 0) + 1);
      });
      const topAssignees = Array.from(assigneeCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      const statusLegend = statusCounts
        .map(
          (entry) => `<div class="legend-item">
        <svg class="swatch-icon" viewBox="0 0 10 10" aria-hidden="true">
          <circle cx="5" cy="5" r="4" fill="${entry.color}"></circle>
        </svg>
        ${escapeHtml(entry.label)}: ${entry.count}
      </div>`,
        )
        .join('');
      const donutCircumference = 2 * Math.PI * 54;
      let statusOffset = 0;
      const statusDonutSegments = statusCounts
        .map((entry) => {
          const ratio = totalItems > 0 ? entry.count / totalItems : 0;
          const dash = ratio * donutCircumference;
          const segment = `<circle cx="70" cy="70" r="54" fill="none" stroke="${entry.color}" stroke-width="18" stroke-linecap="butt" stroke-dasharray="${dash} ${donutCircumference - dash}" stroke-dashoffset="${-statusOffset}" transform="rotate(-90 70 70)"></circle>`;
          statusOffset += dash;
          return segment;
        })
        .join('');
      const statusDonutSvg = `<svg viewBox="0 0 140 140" width="160" height="160" aria-label="Status distribution">
    <circle cx="70" cy="70" r="54" fill="none" stroke="#e2e8f0" stroke-width="18"></circle>
    ${statusDonutSegments}
    <text x="70" y="66" text-anchor="middle" font-size="12" fill="#64748b">Total</text>
    <text x="70" y="86" text-anchor="middle" font-size="20" font-weight="700" fill="#0f172a">${totalItems}</text>
  </svg>`;
      const maxPriorityCount = Math.max(...priorityCounts.map((entry) => entry.count), 1);
      const priorityBarSvg = `<svg viewBox="0 0 360 140" width="100%" height="160" aria-label="Priority mix">
    ${priorityCounts
      .map((entry, index) => {
        const y = 20 + index * 38;
        const width = (entry.count / maxPriorityCount) * 190;
        return `
          <text x="0" y="${y + 11}" font-size="12" fill="#334155">${escapeHtml(entry.label)}</text>
          <rect x="92" y="${y}" width="210" height="16" rx="8" fill="#e2e8f0"></rect>
          <rect x="92" y="${y}" width="${width}" height="16" rx="8" fill="${entry.color}"></rect>
          <text x="315" y="${y + 12}" font-size="12" font-weight="700" fill="#0f172a">${entry.count}</text>
        `;
      })
      .join('')}
  </svg>`;
      const assigneeRows = topAssignees.length
        ? topAssignees
          .map(([name, count]) => `<tr><td>${escapeHtml(name)}</td><td>${count}</td></tr>`)
          .join('')
        : '<tr><td>No assignees</td><td>0</td></tr>';
      const itemRows = reportItems
        .slice(0, 150)
        .map((item) => {
          const images = imagesByItem.get(item.id) ?? [];
          const imagesHtml = images.length
            ? `<div class="image-list">${images
              .map((image) => {
                const proxyUrl = resolvePunchListReportImageUrl(
                  image.url,
                  window.location.origin,
                );
                return `<a class="image-link" href="${escapeHtml(proxyUrl)}" target="_blank" rel="noopener noreferrer" title="Open ${escapeHtml(image.file_name)}">
                <img class="item-thumbnail" src="${escapeHtml(proxyUrl)}" alt="${escapeHtml(image.file_name)}" />
              </a>`;
              })
              .join('')}</div>`
            : '<span class="no-images">—</span>';
          return `<tr>
      <td>${escapeHtml(item.title)}</td>
      <td>${escapeHtml(item.trade?.name ?? '—')}</td>
      <td>${escapeHtml(item.item_type?.name ?? '—')}</td>
      <td>${escapeHtml(STATUS_LABELS[item.status])}</td>
      <td>${escapeHtml(PRIORITY_LABELS[item.priority])}</td>
      <td>${escapeHtml(item.assignee ? `${item.assignee.first_name} ${item.assignee.last_name}`.trim() : 'Unassigned')}</td>
      <td>${escapeHtml(item.location?.name ?? '—')}</td>
      <td>${escapeHtml(formatReportDate(item.due_date))}</td>
      <td>${imagesHtml}</td>
    </tr>`;
        })
        .join('');

      const nowLabel = new Date().toLocaleString();
      const fullReportTitle = `${reportTitle} Report`;
      const projectLine = projectName ? `Project: ${projectName}` : '';
      const logoUrl = `${window.location.origin}/static/images/IronView_Vector.svg`;
      const scopeNote = reportTitle === 'All Punch Items'
        ? 'This is a comprehensive report of all punch items in this project.'
        : `This report includes ${reportTitle.toLowerCase()}.`;
      const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(fullReportTitle)}</title>
<style>
  @page { size: letter; margin: 0.5in; }
  * { box-sizing: border-box; }
  body { font-family: Inter, "Segoe UI", Roboto, Arial, sans-serif; color:#0f172a; margin:0; background:#fff; }
  .header { position:relative; background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%); color:#f8fafc; border-radius:14px; padding:20px; margin-bottom:16px; }
  .logo { position:absolute; right:20px; top:18px; width:200px; height:auto; object-fit:contain; filter:brightness(0) invert(1); }
  .title { font-size:26px; font-weight:800; margin:0; }
  .project-line { margin-top:4px; color:#bfdbfe; font-size:15px; font-weight:600; }
  .subtitle { margin-top:8px; color:#dbeafe; font-size:12px; letter-spacing:0.04em; text-transform:uppercase; }
  .kpis { display:grid; grid-template-columns:repeat(4, minmax(0,1fr)); gap:10px; margin:14px 0; }
  .kpi { border:1px solid #dbeafe; border-radius:10px; padding:10px; background:#f8fafc; }
  .kpi .label { color:#475569; font-size:11px; text-transform:uppercase; letter-spacing:0.04em; }
  .kpi .value { margin-top:4px; font-weight:800; font-size:24px; color:#0f172a; }
  .grid { display:grid; grid-template-columns: 1fr 1fr; gap:10px; margin-bottom:14px; }
  .card { border:1px solid #e2e8f0; border-radius:10px; padding:12px; background:#fff; }
  .card h3 { margin:0 0 10px 0; font-size:13px; text-transform:uppercase; letter-spacing:0.04em; color:#334155; }
  .chart-wrap { display:flex; justify-content:center; align-items:center; min-height:170px; }
  .legend { display:grid; gap:4px; margin-top:8px; }
  .legend-item { font-size:12px; color:#334155; display:flex; align-items:center; gap:6px; }
  .swatch-icon { width:10px; height:10px; display:inline-block; }
  table { width:100%; border-collapse:collapse; }
  th, td { font-size:11px; padding:6px 7px; border-bottom:1px solid #e2e8f0; text-align:left; vertical-align:top; }
  th { background:#f8fafc; color:#334155; font-weight:700; }
  .small-table td:last-child, .small-table th:last-child { text-align:right; }
  .image-list { display:flex; flex-wrap:wrap; gap:5px; min-width:66px; }
  .image-link { display:block; width:46px; height:46px; border:1px solid #cbd5e1; border-radius:6px; overflow:hidden; background:#f8fafc; }
  .item-thumbnail { display:block; width:100%; height:100%; object-fit:cover; }
  .no-images { color:#94a3b8; }
  .footnote { margin-top:8px; font-size:10px; color:#64748b; }
  @media print {
    .image-link { break-inside:avoid; }
    .item-details { break-inside:auto; }
    .item-table { break-inside:auto; }
    .item-table thead { display:table-header-group; }
    .item-table tbody { display:table-row-group; }
    .item-table thead tr { break-inside:avoid; break-after:avoid-page; }
    .item-table tbody tr { break-inside:avoid; page-break-inside:avoid; }
  }
</style>
</head>
<body>
  <div class="header">
    <img class="logo" src="${logoUrl}" alt="Ironview logo" />
    <h1 class="title">${escapeHtml(fullReportTitle)}</h1>
    ${projectLine ? `<div class="project-line">${escapeHtml(projectLine)}</div>` : ''}
    <div class="subtitle">Generated ${escapeHtml(nowLabel)} • ${totalItems} items</div>
  </div>
  <div class="kpis">
    <div class="kpi"><div class="label">Total Items</div><div class="value">${totalItems}</div></div>
    <div class="kpi"><div class="label">Open</div><div class="value">${openItems}</div></div>
    <div class="kpi"><div class="label">Overdue</div><div class="value">${overdueItems}</div></div>
    <div class="kpi"><div class="label">Completion Rate</div><div class="value">${completionRate}%</div></div>
  </div>
  <div class="grid">
    <div class="card">
      <h3>Status Distribution</h3>
      <div class="chart-wrap">${statusDonutSvg}</div>
      <div class="legend">${statusLegend}</div>
    </div>
    <div class="card">
      <h3>Priority Mix</h3>
      <div class="chart-wrap">${priorityBarSvg}</div>
    </div>
  </div>
  <div class="grid">
    <div class="card">
      <h3>Top Assignees</h3>
      <table class="small-table">
        <thead><tr><th>Assignee</th><th>Items</th></tr></thead>
        <tbody>${assigneeRows}</tbody>
      </table>
    </div>
    <div class="card">
      <h3>Snapshot Notes</h3>
      <p style="font-size:12px; line-height:1.5; margin:0; color:#334155;">
        ${escapeHtml(scopeNote)}
      </p>
    </div>
  </div>
  <div class="card item-details">
    <h3>Item Details ${reportItems.length > 150 ? `(showing first 150 of ${reportItems.length})` : ''}</h3>
    <table class="item-table">
      <thead><tr><th>Title</th><th>Trade</th><th>Type</th><th>Status</th><th>Priority</th><th>Assignee</th><th>Location</th><th>Due Date</th><th>Images</th></tr></thead>
      <tbody>${itemRows || '<tr><td colspan="9">No items</td></tr>'}</tbody>
    </table>
    <div class="footnote">Tip: In the print dialog, choose "Save as PDF".</div>
  </div>
  <script>
    window.onload = function () { window.focus(); window.print(); };
  </script>
</body>
</html>`;

      reportWindow.document.open();
      reportWindow.document.write(html);
      reportWindow.document.close();
    },
    [],
  );

  return { exportItemsToCsv, generatePunchListPdfReport };
};
